import { ref } from 'vue';

import type { PushKeyDto } from '@/api/generated/models/index.js';

import {
  notificationsControllerPushKeyV1,
  notificationsControllerPushSubscribeV1,
  notificationsControllerPushUnsubscribeV1,
} from '@/api/generated/notifications/notifications.js';

/**
 * Пуш-уведомления на этом устройстве:
 *  - unsupported   — браузер не умеет;
 *  - needs-install — iPhone/iPad: пуш приходит только иконке на экране «Домой»
 *                    в полноэкранном режиме (правило Apple, iOS 16.4+);
 *  - server-off    — на сервере не заданы ключи;
 *  - denied        — человек запретил уведомления в настройках браузера;
 *  - off / on      — можно включить / включены.
 */
export type PushState = 'unknown' | 'unsupported' | 'needs-install' | 'server-off' | 'denied' | 'off' | 'on';

const state = ref<PushState>('unknown');
const busy = ref(false);
let publicKey: string | null = null;

const isIos = (): boolean =>
  /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1);
const isStandalone = (): boolean =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;
const supported = (): boolean => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

function keyBytes(base64: string): Uint8Array {
  const pad = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

async function registration(): Promise<ServiceWorkerRegistration> {
  return (await navigator.serviceWorker.getRegistration('/')) ?? navigator.serviceWorker.register('/sw.js', { scope: '/', updateViaCache: 'none' });
}

async function send(sub: PushSubscription): Promise<void> {
  const json = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) throw new Error('Пустая подписка');
  await notificationsControllerPushSubscribeV1({ endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth } });
}

/**
 * Узнать состояние. Если разрешение уже дано и подписка в браузере есть —
 * заново привязываем её к вошедшему сотруднику (после выхода/входа или если
 * сервер её забыл).
 */
async function refresh(): Promise<void> {
  try {
    if (isIos() && !isStandalone()) {
      state.value = 'needs-install';
      return;
    }
    if (!supported()) {
      state.value = 'unsupported';
      return;
    }
    publicKey ??= ((await notificationsControllerPushKeyV1()) as unknown as PushKeyDto).publicKey;
    if (!publicKey) {
      state.value = 'server-off';
      return;
    }
    if (Notification.permission === 'denied') {
      state.value = 'denied';
      return;
    }
    const sub = Notification.permission === 'granted' ? await (await registration()).pushManager.getSubscription() : null;
    if (sub) {
      await send(sub);
      state.value = 'on';
    } else {
      state.value = 'off';
    }
  } catch {
    state.value = supported() ? 'off' : 'unsupported';
  }
}

/** Включить: спросить разрешение (только по нажатию кнопки) и подписаться. */
async function enable(): Promise<void> {
  if (!publicKey) return;
  busy.value = true;
  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      state.value = permission === 'denied' ? 'denied' : 'off';
      return;
    }
    const reg = await registration();
    const sub =
      (await reg.pushManager.getSubscription()) ??
      (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) as BufferSource }));
    await send(sub);
    state.value = 'on';
  } finally {
    busy.value = false;
  }
}

/** Выключить на этом устройстве. */
async function disable(): Promise<void> {
  busy.value = true;
  try {
    const sub = await (await registration()).pushManager.getSubscription();
    if (sub) {
      await notificationsControllerPushUnsubscribeV1({ endpoint: sub.endpoint }).catch(() => undefined);
      await sub.unsubscribe();
    }
    state.value = 'off';
  } finally {
    busy.value = false;
  }
}

/** При выходе: сервер перестаёт слать пуш этому устройству (разрешение браузера остаётся). */
export async function forgetPushOnLogout(): Promise<void> {
  try {
    if (!supported()) return;
    const sub = await (await navigator.serviceWorker.getRegistration('/'))?.pushManager.getSubscription();
    if (sub) await notificationsControllerPushUnsubscribeV1({ endpoint: sub.endpoint });
  } catch {
    /* выходим в любом случае */
  }
}

/** Число на иконке приложения (там, где браузер это умеет). */
export function setAppBadge(unread: number): void {
  const nav = navigator as Navigator & { setAppBadge?: (n: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
  try {
    if (unread > 0) void nav.setAppBadge?.(unread).catch(() => undefined);
    else void nav.clearAppBadge?.().catch(() => undefined);
  } catch {
    /* не поддерживается */
  }
}

export function usePush() {
  return { state, busy, refresh, enable, disable };
}
